import { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'rsuite';
import { Reload } from '@rsuite/icons';
import { useAuth } from '../utils/useAuth';
import { getApiUrl, getAuthHeaders, getUserId } from '../utils/apiConfig';

function LogDialog({ show, onClose, jobId }) {
  const auth = useAuth();
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!jobId) return;
    
    setLogsLoading(true);
    setError(null);
    
    const userId = getUserId(auth.user);
    if (!userId) {
      setLogs('Error: User ID not found. Please set up your user ID first.');
      setLogsLoading(false);
      return;
    }
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(getApiUrl(`/users/${userId}/jobs/${jobId}/logs`), {
        method: 'GET',
        headers: headers,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch logs');
      }
      
      const data = await response.json();
      setLogs(data.logs || 'No logs available');
      
      // Scroll to bottom after logs are loaded
      setTimeout(() => {
        const logsPre = document.getElementById('logs-content');
        if (logsPre) {
          logsPre.scrollTop = logsPre.scrollHeight;
        }
      }, 200);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogs(`Error: ${err.message}`);
      setError(err.message);
    } finally {
      setLogsLoading(false);
    }
  }, [jobId, auth.user, auth.getAccessTokenSilently]);

  // Fetch logs when dialog opens or jobId changes
  useEffect(() => {
    if (show && jobId) {
      fetchLogs();
    } else {
      // Reset state when dialog closes
      setLogs('');
      setError(null);
    }
  }, [show, jobId, fetchLogs]);

  const handleRefresh = () => {
    fetchLogs();
  };

  return (
    <Modal 
      open={show} 
      onClose={onClose}
      size="lg"
      fullscreen
    >
      <Modal.Header>
        <Modal.Title>Logs for Job {jobId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {logsLoading ? (
          <div>Loading logs...</div>
        ) : (
          <pre 
            id="logs-content"
            style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              maxHeight: '70vh',
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}
          >
            {logs || 'No logs available'}
          </pre>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
          onClick={handleRefresh} 
          appearance="subtle"
          icon={<Reload />}
          loading={logsLoading}
          disabled={logsLoading}
        >
          Refresh
        </Button>
        <Button onClick={onClose} appearance="primary">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default LogDialog;

